/**
 * The `WriteOptions` a download uses. One definition, used by the format-step
 * page and the tool shell alike.
 *
 * ── WHY THIS FILE EXISTS, AND WHERE IT SHOULD LIVE INSTEAD ────────────────
 * `WriteOptions` has twelve required fields and **nothing shipped exports a
 * default set.** The canonical one is real and complete — it is
 * `writeOptions()` in `test/convert/_fixtures/ctx.ts` — but a test fixture is
 * not an API, and `web/` cannot import from `test/`.
 *
 * This is the second time the same shape of gap has cost a round: the first
 * was `resegmentOp`, which existed in `src/resegment/op.ts` and was not
 * re-exported from the barrel, so from a consumer's side it did not exist.
 * Here it exists and lives somewhere no consumer can reach. Both are the same
 * rule from a different angle — **a module's public surface is what it
 * exports, and a default that only its own tests can construct is not a
 * default.** Raised for CONVERT in comms/UI.md.
 *
 * ── WHY IT WAS SAFE TO WRITE THIS AND NOT `resegmentOp` ───────────────────
 * I refused to author `ResegmentOp` because its fifteen fields are empirical
 * thresholds — cue durations, CPS ceilings, silence gaps — that nobody could
 * derive from the contract and a wrong guess would silently mis-cut every
 * transcript on the site.
 *
 * These are not that. Every field below is either documented in
 * `contracts/cue-model.ts` with its own default in the comment (`null = all`,
 * "Off by default", `null = format defaults`) or is a two-value interop
 * choice. Nothing here is a measured number. The two real decisions are
 * called out where they are made.
 */

import type { Framerate, WriteFormat, WriteOptions } from '../../../../../contracts/cue-model.ts';
import { rowFor } from '../../../../../src/formats/capabilities.ts';

/** The rate an SCC download is written at: 29.97, with drop-frame timecode. */
export const SCC_RATE: Framerate = { num: 30000, den: 1001, label: '29.97' };

/**
 * Line endings for a DOWNLOADED file.
 *
 * The contract's own words: *"SRT in the wild is CRLF. Explicit, never
 * platform-dependent."* A subtitle file's job is to load in whatever the user
 * drops it into — desktop players, editors, YouTube's uploader — and CRLF is
 * what that population handles without complaint. The test fixtures use `\n`
 * for a readable golden diff, which is the right choice there and the wrong
 * one here.
 */
const DOWNLOAD_EOL: WriteOptions['eol'] = '\r\n';

export interface DownloadWriteOverrides {
  /** Set for `sub` and `scc`, which REFUSE rather than assume one. */
  readonly framerate?: WriteOptions['framerate'];
  readonly formatOptions?: WriteOptions['formatOptions'];
  /**
   * Set only by the save step's "Text encoding" control, which shows for SMI
   * (contract 1.7.0) and for SRT with Korean subtitles (1.8.0). Absent means
   * UTF-8, so every download that never shows the control is byte-for-byte
   * what it was before.
   */
  readonly encoding?: WriteOptions['encoding'];
}

/**
 * The options a download uses, for any format.
 *
 * `framerate: null` is deliberate and is the contract's design: `sub` and
 * `scc` refuse to write without one rather than defaulting to 23.976, which
 * is wrong for every PAL source and is the exact competitor bug this product
 * exists to beat. A refusal surfaces as a typed `framerate-required` failure
 * and the UI asks for the number.
 */
export function downloadWriteOptions(
  format: WriteFormat,
  over: DownloadWriteOverrides = {},
): WriteOptions {
  return {
    format,
    framerate: over.framerate ?? null,
    // null = every track. A transcript has one; a merged document renders all,
    // which is what the user asked for by merging them.
    trackId: null,
    // 'where-available' is the contract's stated DEFAULT: emit per-word timing
    // on cues that have full coverage, plain text elsewhere. 'require' would
    // fail the whole write on one uncovered cue, which is a karaoke-authoring
    // policy and not a download policy.
    wordTiming: 'where-available',
    // null = honour each cue's own karaoke kind rather than overriding it.
    karaoke: null,
    // null = emit as-is. Clamping lines is a cleaning decision the user makes
    // on a tool page, not something a download should do behind their back.
    maxLines: null,
    // "Off by default; some players choke."
    anchorAsSrtTag: false,
    // The BOM is a separate compatibility switch and it breaks as many players
    // as it fixes. (It means nothing in EUC-KR, and the library ignores it there.)
    bom: false,
    // UTF-8 unless the user picked otherwise for SMI or Korean SRT: current
    // players read it, and it is the encoding that loses nothing. Never switched
    // automatically, not even for Korean text (SEO_BRIEF_SMI.md §2.5).
    encoding: over.encoding ?? 'utf-8',
    eol: DOWNLOAD_EOL,
    // Pure fidelity optimisation, and it only applies when the write format
    // equals the source format. Off: a transcript has no source format to pass
    // through, and on the converter path the user asked us to change something.
    useRawPassthrough: false,
    formatOptions: over.formatOptions ?? null,
  };
}

/**
 * The rate a tool page's download in `format` is counted in: the rate the file
 * was READ at (the one it declares, or the one typed into the FPS prompt), and
 * only for a format that counts in frames. Null everywhere else, which is what
 * every other download has always sent.
 *
 * A MicroDVD file written back as MicroDVD needs a rate, and the library
 * refuses rather than assume one (`framerate-required`). The page already
 * asked for the rate to read the file, so it uses it again: that is how
 * /fix-subtitle-encoding hands a .sub back as a .sub, and a shift or a clean-up
 * does too. Not on a page whose ops change the rate (`framerate`, the FPS
 * converter), where the file's own rate is the wrong one to count the result
 * in; that page keeps refusing rather than writing frames at the old rate.
 */
export function writeFramerate(
  format: WriteFormat,
  read: Framerate | null,
  ops: readonly string[],
): Framerate | null {
  // SCC is defined at 29.97 fps (contract 1.13.0), whatever the file was read at.
  if (format === 'scc') return SCC_RATE;
  if (read === null || ops.includes('framerate')) return null;
  try {
    return rowFor(format).requiresFramerate ? read : null;
  } catch {
    return null; // no capability row: not a format this page can promise frames for
  }
}
